import {
  createRouter,
  createWebHashHistory,
  RouteLocationRaw,
} from 'vue-router';
import NProgress from 'nprogress'; // progress bar
import 'nprogress/nprogress.css';

import { useTabBarStore } from '@/store';
import {
  DEFAULT_ROUTE_NAME,
  DEFAULT_SUPPLIER_ROUTE_NAME,
  SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME,
  SUPPLIER_LOGIN_ROUTE_NAME,
  // SUPPLIER_LOGIN_ROUTE_NAME,
} from '@/router/constants';
import { getQueryVariable } from '@/utils/common';
import { isLoginBySupplier } from '@/store/modules/supplier/auth';
import { Factory } from '@/router/routes/factoryRoutes';
import {
  getScrollPosition,
  scrollToPosition,
} from '@/utils/scroll-position-listener';
import { appRoutes, electronRoutes } from './routes';
import {
  REDIRECT_MAIN,
  NOT_FOUND_ROUTE,
  NOT_BOUND_CLIENT_ROUTE,
  Route404,
} from './routes/base';
import createRouteGuard from './guard';

NProgress.configure({ showSpinner: false }); // NProgress Configuration

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      redirect: () => {
        if (
          isLoginBySupplier() ||
          (getQueryVariable('state') as string) ===
            SUPPLIER_LOGIN_REDIRECT_ROUTE_NAME
        )
          return { name: DEFAULT_SUPPLIER_ROUTE_NAME };
        return { name: DEFAULT_ROUTE_NAME };
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/login/index.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    // 加工厂首页路由
    {
      path: '/supplier-login',
      name: SUPPLIER_LOGIN_ROUTE_NAME,
      component: () => import('@/views/login/index.vue'),
      meta: {
        requiresAuth: false,
      },
      props: () => ({
        mode: 'supplier',
      }),
    },
    // 加工厂主页路由，用于测试（删）
    // {
    //   path: '/factory/production-order',
    //   name: DEFAULT_SUPPLIER_ROUTE_NAME, // 修改这个默认主页路由
    //   component: () => import('@/views/factoryPages/productionOrder/index.vue'),
    //   meta: {
    //     requiresAuth: false,
    //   },
    // },
    ...appRoutes,
    ...electronRoutes,
    Factory,
    REDIRECT_MAIN,
    NOT_FOUND_ROUTE,
    NOT_BOUND_CLIENT_ROUTE,
    Route404,
  ],
  scrollBehavior(to, from, savedPosition) {
    // 如果有 scrollTo 参数，不执行滚动位置恢复逻辑，由组件内部控制 ---- 兼容跳转至指定位置的功能
    // noScrollStorage 新增页需要该字段，表示不需要使用记住的滚动位置
    if (to.query.scrollTo || to.meta?.noScrollStorage === true) {
      return false;
    }
    if (
      savedPosition &&
      !(savedPosition.top === 0 && savedPosition.left === 0)
    ) {
      // 使用浏览器前进后退时，只有在滚动位置 不是{top: 0, left: 0} 时才使用
      // 使用scrollToPosition是因为真正的滚动元素并不是window，而是#layout-content
      scrollToPosition({ scrollTop: savedPosition.top || 0 });
      return savedPosition;
    }
    // 从存储中恢复滚动位置
    const storedPosition = getScrollPosition(to.fullPath);
    if (storedPosition) {
      scrollToPosition(storedPosition);
      return { top: storedPosition.scrollTop };
    }
    scrollToPosition({ scrollTop: 0 });
    return { top: 0 };
  },
});

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    const redirect: Location | (string & Location) = (to as any).href
      ? (to as any).href
      : `#${to.fullPath}`;
    window.location = redirect;
  }
});
createRouteGuard(router);

const { push } = router;
const tabBarStore = useTabBarStore();
router.push = (to: RouteLocationRaw) => {
  tabBarStore.setPageLoading(true);
  return new Promise((resolve) => {
    if (window.electronAPI?.singlePage) {
      window.electronAPI?.routeTo(JSON.stringify(to));
    }
    setTimeout(async () => {
      resolve(await push.call(router, to));
    }, 50);
  });
};

export default router;
